import React, { useEffect, useMemo } from 'react';
import Layout from '@shared/ui/organisms/layout/Layout';
import { VerticalSpacer } from '@shared/ui/atoms/VerticalSpacer';
import { ContentPage, MainContent, Sidebar } from "../ViewComponents";
import { BiggerThanTabletOnly } from '@shared/ui/atoms/DeviceSpecific';
import { OfficeListSidebar } from "../daily-office-list-view/OfficeListSidebar";
import styled from "styled-components";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { VisitorBookingHeader } from "./components/VisitorBookingHeader";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { selectAllOfficeEntities } from "@features/desks/store/advanceHotDeskingSetup.duck";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { selectOffice, selectSelectedOffice } from "../../../../store/officeView.duck";
import Calendar from '@shared/ui/molecules/calendar/Calendar';
import { selectCalendarDate, setCalendarDate } from "@shared/store/dashboard.duck";
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { DialogIdentifiers, openDialogWithPayload } from "@shared/store/dialog.duck";
import { VisitorBooking, VisitorSignInState } from "./data/visitor-booking-service";
import { DATE_FORMAT } from "@shared/utils/DateUtils";
import { VisitorBookingLine } from "./components/VisitorBookingLine";
import { loadVisitorBookings, selectVisitorBookings, selectVisitorBookingsLoading } from "./data/visitorBooking.duck";
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { useTranslation } from "react-i18next";
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { NoVisitorBookings } from "./components/NoVisitorBookings";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { VisitorBookingColumnHeaders } from "./components/VisitorBookingColumnHeaders";
import { getRootOfficeById } from "@shared/utils/OfficeHelper";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { canUserEditVisitorBookings } from "@shared/utils/AccessControl";

export function VisitorBookingView(props: Props) {
  document.body.style.backgroundColor = '#F2F6FF';
  const {t} = useTranslation();
  const allOffices = useSelector(selectAllOfficeEntities);
  const params: any = useParams();
  const calendarDate = useSelector(selectCalendarDate);
  const calendarDateString = calendarDate.format(DATE_FORMAT);
  const dispatch = useDispatch();
  const visitorBookings = useSelector(selectVisitorBookings);
  const selectedOffice = useSelector(selectSelectedOffice);
  const loading = useSelector(selectVisitorBookingsLoading);
  const currentUser = useSelector(selectCurrentUser);

  const canEditVisitorBookings = canUserEditVisitorBookings(currentUser);

  const officeId = selectedOffice?.id
  const noBookings = !loading && visitorBookings.length === 0;

  const dueInVisitors = useMemo(() => {
    return visitorBookings.filter(vb => vb.signInState !== VisitorSignInState.CHECKED_OUT && vb.signInState !== VisitorSignInState.CHECKED_IN)
  }, [visitorBookings]);

  const signedInVisitors = useMemo(() => {
    return visitorBookings.filter(vb => vb.signInState === VisitorSignInState.CHECKED_IN)
  }, [visitorBookings]);

  const signedOutVisitors = useMemo(() => {
    return visitorBookings.filter(vb => vb.signInState === VisitorSignInState.CHECKED_OUT)
  }, [visitorBookings]);

  const office = useMemo(() => {
    const officeId = parseInt(params.officeId)
    return allOffices.find((o: OfficeEntity) => o.id === officeId);
  }, [params, allOffices]);

  const officeHasAnyChildOffices = useMemo(() => {
    if (!selectedOffice) return false;
    const rootOffice = getRootOfficeById(allOffices, selectedOffice.id);
    const directChildren = allOffices.filter((o: OfficeEntity) => o.parentId === rootOffice?.id);
    return directChildren.length > 0;
  }, [allOffices, selectedOffice]);

  useEffect(() => {
    if (office) {
      dispatch(selectOffice(office));
    }
  }, [dispatch, office]);

  useEffect(() => {
    if (officeId && calendarDateString) {
      dispatch(loadVisitorBookings({date: calendarDateString, officeId: officeId}))
    }
  }, [calendarDateString, dispatch, officeId]);

  if (!office) {
    return null;
  }

  return (
    <Layout>
      <DailyVisitorContainer>
        <DailyVisitorListViewContent>

          {selectedOffice && <VisitorBookingHeader office={selectedOffice} />}

          {loading ? <>
            <Center><LoadingSpinner hideBorder={true} hideText={true} /></Center>
          </> : <>
            {noBookings ? <>
              <NoVisitorBookings />
            </> : <>

              <VisitorBookingColumnHeaders />

              {dueInVisitors.length > 0 && <>
                <HeadlineSmall style={{display: 'flex', marginBottom: 12}}><Icon style={{marginRight: 8}} icon={IconTypes.Time} />{t('visitor-booking.due-today')}</HeadlineSmall>
                <VisitorBookingsList style={{marginBottom: 24}}>
                  {dueInVisitors.map((booking: VisitorBooking, key: number) => <VisitorBookingLine canUserEdit={canEditVisitorBookings} booking={booking} officeHasAnyChildOffice={officeHasAnyChildOffices} key={key} />)}
                </VisitorBookingsList>
              </>}

              {signedInVisitors.length > 0 && <>
                <HeadlineSmall style={{display: 'flex', marginBottom: 12}}><Icon style={{marginRight: 8}} icon={IconTypes.Tick} />{t('visitor-booking.signed-in')}</HeadlineSmall>
                <VisitorBookingsList style={{marginBottom: 24}}>
                  {signedInVisitors.map((booking: VisitorBooking, key: number) => <VisitorBookingLine canUserEdit={canEditVisitorBookings} booking={booking} officeHasAnyChildOffice={officeHasAnyChildOffices} key={key} />)}
                </VisitorBookingsList>
              </>}

              {signedOutVisitors.length > 0 && <>
                <HeadlineSmall style={{display: 'flex', marginBottom: 12}}><Icon style={{marginRight: 8}} icon={IconTypes.Tick} />{t('visitor-booking.signed-out')}</HeadlineSmall>
                <VisitorBookingsList style={{marginBottom: 24}}>
                  {signedOutVisitors.map((booking: VisitorBooking, key: number) => <VisitorBookingLine canUserEdit={canEditVisitorBookings} booking={booking} officeHasAnyChildOffice={officeHasAnyChildOffices} key={key} />)}
                </VisitorBookingsList>
              </>}

            </>}
          </>}

        </DailyVisitorListViewContent>
        <VerticalSpacer/>
        <Sidebar>
          <Calendar onDateSelect={(date) => dispatch(setCalendarDate(date))}
                    activeDate={calendarDate}
                    initialDate={calendarDate}
                    style={{marginBottom: 24}} />

          <BiggerThanTabletOnly>
            <OfficeListSidebar hideUsageChart={true} />
          </BiggerThanTabletOnly>

          <PrimaryButton fullWidth={true} click={() => dispatch(openDialogWithPayload({
            payload: { date: calendarDate, office: selectedOffice },
            activeDialog: DialogIdentifiers.AddVisitorDialog
          }))} text={'visitor-booking.add-visitor'} />
        </Sidebar>
      </DailyVisitorContainer>
    </Layout>
  )
}

interface Props {
}

const DailyVisitorContainer = styled(ContentPage)`
  flex-direction: column;
  min-height: 60vh;
  @media (${tablet}) {
    flex-direction: row;
  }
`

const DailyVisitorListViewContent = styled(MainContent)`
  padding-top: 0;
  min-height: 700px;
`

const VisitorBookingsList = styled.div`
  @media (${tablet}) {
    margin: 0 24px 0 0;
  }
`
